import React, { useState, useEffect } from 'react';
import Strings from '../../../translations/strings';
import { getRssFeed } from '../../../api/rssFeedApi';
import styles from './RssFeed.module.css';
import { stripHtml, formatDateString } from '../../../helpers/helpers';
import RssFeedItem from '../RssFeedItem/RssFeedItem';
import ArticleLoader from '../ArticleLoader/ArticleLoader';
import { Fabric } from '@fluentui/react/lib/Fabric';
import { mergeStyles } from '@fluentui/react/lib/Styling';

const wrapperClass = mergeStyles({
    selectors: {
        '& > .ms-Shimmer-container:not(:last-of-type)': {
            margin: '10px 0 50px 0',
        },
    },
});

interface IRssFeedProps {
    language: string;
}

interface IFeedResponse {
    rss: {
        channel: {
            item: IFeedItem[];
        };
    };
}

export interface ITableItem {
    key: string;
    name: string;
    value: string;
    img: string;
    title: string;
    link: string;
    description: string;
    creator: string;
    pubDate: string;
    category: string;
}

export interface IFeedItem {
    'dc:creator': { _cdata: string };
    description: { _cdata: string };
    link: { _text: string };
    title: { _text: string };
    pubDate: { _text: string };
    'media:content': {
        _attributes: {
            url: string;
        };
    };
    category: { _cdata: string } | { _cdata: string }[];
}

const truncateDescription = (description: string) => {
    // Delete last part of description:
    let desc = description.split('<p>The post <a')[0];
    // Remove html tags:
    desc = stripHtml(desc);
    // Delete interpunction and whitespaces at the end:
    desc = desc.replace(/\W*\s(\S)*$/, '');
    return desc + '...';
};

const RssFeed: React.FC<IRssFeedProps> = ({ language }) => {
    const [feedItems, setFeedItems] = useState<any>(null);
    const [feedError, setFeedError] = useState<string | null>(null);
    const [isDataLoaded, setIsDataLoaded] = useState(false);

    const getFeedSuccess = (data: IFeedResponse) => {
        // Get only first 5 items from rss feed
        setFeedItems(data.rss.channel.item.slice(0, 5));
        setIsDataLoaded(true);
    };

    const getFeedError = (err: any) => {
        console.log(err);
        setFeedError(Strings.components.rssFeed.feedError);
    };

    useEffect(() => {
        setIsDataLoaded(false);
        getRssFeed(getFeedSuccess, getFeedError);
    }, [language]);

    const items = feedItems?.map((item: IFeedItem) => {
        return {
            key: item.title?._text,
            title: item.title?._text,
            description: truncateDescription(item.description?._cdata),
            img: item['media:content']?._attributes.url,
            link: item.link?._text,
            pubDate: formatDateString(item.pubDate?._text),
            creator: item['dc:creator']?._cdata,
            category: Array.isArray(item.category) ? item.category[0]._cdata : item.category?._cdata,
        };
    });

    return (
        <section className={styles.feedContainer}>
            <h2 className={styles.title}>{Strings.components.rssFeed.title}</h2>
            {feedError ? (
                <div className={styles.feedError}>{feedError}</div>
            ) : (
                <Fabric className={!isDataLoaded ? wrapperClass : undefined}>
                    <ArticleLoader isDataLoaded={isDataLoaded}>
                        {items?.map((item: ITableItem) => (
                            <RssFeedItem key={item.key} item={item} />
                        ))}
                    </ArticleLoader>
                    <ArticleLoader isDataLoaded={isDataLoaded} />
                    <ArticleLoader isDataLoaded={isDataLoaded} />
                </Fabric>
            )}
        </section>
    );
};

export default RssFeed;
